import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import "../../styles/lab/lab.css";
import { getSessions } from "../../services/infoCentre/session_formation_service";
import authService from "../../services/auth/authService";
import {
  TYPES_PRESENCE,
  getFeuilleAppel,
  getPresences,
  saisirAppel,
} from "../../services/lab/pedagogieService";

const pad2 = (n) => String(n).padStart(2, "0");
// Date locale (toISOString donnerait la date UTC, fausse après minuit).
const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const libelleSeance = (se) => {
  const [a, m, j] = se.date.split("-").map(Number);
  const jour = new Date(a, m - 1, j).toLocaleDateString("fr-FR", {
    weekday: "short", day: "2-digit", month: "2-digit", year: "numeric",
  });
  return `${jour} — ${se.heure_debut.slice(0, 5)} → ${se.heure_fin.slice(0, 5)}`;
};

/** La séance du jour, sinon la prochaine, sinon la dernière passée. */
const seanceParDefaut = (seances) => {
  const auj = aujourdhui();
  return seances.find((se) => se.date >= auj) || seances[seances.length - 1];
};

/** Feuille d'appel : formateurs (leurs sessions) et administration. */
function FeuilleAppel() {
  const [sessions, setSessions]   = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [date, setDate]           = useState(aujourdhui());

  const [feuille, setFeuille]     = useState([]);
  const [chargement, setChargement] = useState(false);
  const [envoi, setEnvoi]         = useState(false);
  const [flash, setFlash]         = useState(null);
  const [recherche, setRecherche] = useState("");

  const notifier = (msg, type = "succes") => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 4000);
  };

  useEffect(() => {
    getSessions()
      .then((s) => {
        setSessions(s);
        if (s.length) setSessionId(String(s[0].id));
      })
      .catch(() => notifier("Impossible de charger les sessions.", "erreur"));
  }, []);

  // Séances de la session choisie : l'appel se fait à leurs dates.
  const seances = useMemo(
    () => sessions.find((s) => String(s.id) === String(sessionId))?.seances || [],
    [sessions, sessionId]
  );
  useEffect(() => {
    if (seances.length) setDate(seanceParDefaut(seances).date);
  }, [seances]);

  const chargerFeuille = useCallback(async () => {
    if (!sessionId || !date) return;
    setChargement(true);
    try {
      setFeuille(await getFeuilleAppel(sessionId, date));
    } catch {
      notifier("Impossible de charger la feuille d'appel.", "erreur");
      setFeuille([]);
    } finally {
      setChargement(false);
    }
  }, [sessionId, date]);

  useEffect(() => { chargerFeuille(); }, [chargerFeuille]);

  const marquer = (inscriptionId, type) => {
    setFeuille((f) => f.map((l) =>
      l.inscription_id === inscriptionId ? { ...l, type_presence: type } : l));
  };

  const marquerTous = (type) => {
    setFeuille((f) => f.map((l) => ({ ...l, type_presence: type })));
  };

  const enregistrer = async () => {
    const lignes = feuille.filter((l) => l.type_presence);
    if (!lignes.length) {
      notifier("Aucune présence à enregistrer.", "erreur");
      return;
    }
    setEnvoi(true);
    try {
      // Une seule requête pour toute la classe.
      const r = await saisirAppel(date, lignes.map((l) => ({
        inscription: l.inscription_id,
        type_presence: l.type_presence,
        justification: l.justification || "",
      })));
      notifier(r.message || "Appel enregistré.");
      chargerFeuille();
    } catch (e) {
      notifier(e.response?.data?.error || "Enregistrement impossible.", "erreur");
    } finally {
      setEnvoi(false);
    }
  };

  const filtree = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return feuille;
    return feuille.filter((l) =>
      (l.apprenant_nom || "").toLowerCase().includes(q) ||
      (l.matricule || "").toLowerCase().includes(q));
  }, [feuille, recherche]);

  const compte = useMemo(() => {
    const c = { present: 0, retard: 0, absent: 0, vide: 0 };
    feuille.forEach((l) => {
      if (l.type_presence) c[l.type_presence] = (c[l.type_presence] || 0) + 1;
      else c.vide += 1;
    });
    return c;
  }, [feuille]);

  const sessionCourante = sessions.find((s) => String(s.id) === String(sessionId));

  return (
    <Layout>
      <div className="lab-page">
        <header className="lab-header">
          <div>
            <h1><i className="fa-solid fa-clipboard-check"></i> Présences</h1>
            <p className="lab-sous-titre">
              Feuille d'appel — une saisie pour toute la classe
            </p>
          </div>
          {!!feuille.length && (
            <button className="btn-primaire" onClick={enregistrer} disabled={envoi}>
              <i className="fa-solid fa-floppy-disk"></i>
              {envoi ? " Enregistrement…" : " Enregistrer l'appel"}
            </button>
          )}
        </header>

        {flash && <div className={`lab-flash ${flash.type}`}>{flash.msg}</div>}

        <div className="lab-barre">
          <label className="lab-champ">
            Session
            <select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.intitule_session} — {s.statut_display}
                </option>
              ))}
            </select>
          </label>
          <label className="lab-champ">
            {seances.length ? "Séance" : "Date"}
            {seances.length ? (
              <select value={date} onChange={(e) => setDate(e.target.value)}>
                {/* Une option par date : l'appel est enregistré par jour. */}
                {seances.filter((se, i) => seances.findIndex((x) => x.date === se.date) === i).map((se) => (
                  <option key={se.date} value={se.date}>{libelleSeance(se)}</option>
                ))}
              </select>
            ) : (
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            )}
          </label>
          <div className="lab-recherche">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input placeholder="Rechercher un apprenant…" value={recherche}
                   onChange={(e) => setRecherche(e.target.value)} />
          </div>
        </div>

        {sessionCourante && (
          <div className="lab-contexte">
            <span><i className="fa-solid fa-book-open"></i> {sessionCourante.formation_nom}</span>
            {sessionCourante.salle_nom && (
              <span><i className="fa-solid fa-door-open"></i> {sessionCourante.salle_nom}</span>
            )}
            <span><i className="fa-solid fa-users"></i> {feuille.length} inscrit(s)</span>
          </div>
        )}

        {!!feuille.length && (
          <div className="appel-resume">
            {TYPES_PRESENCE.map((t) => (
              <div key={t.value} className="appel-compteur">
                <span className="appel-pastille" style={{ background: t.couleur }}></span>
                {t.label} : <strong>{compte[t.value] || 0}</strong>
              </div>
            ))}
            {compte.vide > 0 && (
              <div className="appel-compteur non-saisi">
                Non saisi : <strong>{compte.vide}</strong>
              </div>
            )}
            <div className="appel-tout">
              Tout marquer :
              {TYPES_PRESENCE.map((t) => (
                <button key={t.value} onClick={() => marquerTous(t.value)}
                        style={{ color: t.couleur }} title={`Tous ${t.label.toLowerCase()}`}>
                  <i className={`fa-solid ${t.icone}`}></i>
                </button>
              ))}
            </div>
          </div>
        )}

        {chargement && <div className="lab-etat"><i className="fa-solid fa-spinner fa-spin"></i> Chargement…</div>}

        {!chargement && !feuille.length && (
          <div className="lab-vide">
            <i className="fa-regular fa-clipboard"></i>
            <p>Aucun apprenant inscrit à cette session.</p>
          </div>
        )}

        {!chargement && !!feuille.length && (
          <div className="appel-liste">
            {filtree.map((l) => (
              <div key={l.inscription_id} className={`appel-ligne ${l.type_presence || "vide"}`}>
                <div className="appel-identite">
                  <strong>{l.apprenant_nom}</strong>
                  {l.matricule && <span className="appel-matricule">{l.matricule}</span>}
                  {l.saisie && <span className="appel-deja"><i className="fa-solid fa-check"></i> déjà saisi</span>}
                </div>
                <div className="appel-boutons">
                  {TYPES_PRESENCE.map((t) => (
                    <button
                      key={t.value}
                      className={l.type_presence === t.value ? "actif" : ""}
                      style={l.type_presence === t.value
                        ? { background: t.couleur, borderColor: t.couleur, color: "#fff" }
                        : undefined}
                      onClick={() => marquer(l.inscription_id, t.value)}
                    >
                      <i className={`fa-solid ${t.icone}`}></i> {t.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {!filtree.length && (
              <div className="lab-vide"><p>Aucun apprenant ne correspond.</p></div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}


const dateFr = (iso) => {
  const [a, m, j] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(a, m - 1, j).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
};

/**
 * « Mes présences » : la vue de l'étudiant, en lecture seule. Le serveur ne
 * lui renvoie que ses propres présences (scope_presences) ; la feuille
 * d'appel de la classe lui est fermée.
 */
function MesPresences() {
  const [presences, setPresences] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    getPresences()
      .then((p) => setPresences(Array.isArray(p) ? p : (p?.results ?? [])))
      .catch(() => setErreur(true))
      .finally(() => setChargement(false));
  }, []);

  const parSession = useMemo(() => {
    const groupes = new Map();
    presences.forEach((p) => {
      if (!groupes.has(p.session_id)) {
        groupes.set(p.session_id, { nom: p.session_nom, formation: p.formation_nom, lignes: [],
                                    compte: { present: 0, retard: 0, absent: 0 } });
      }
      const g = groupes.get(p.session_id);
      g.lignes.push(p);
      if (g.compte[p.type_presence] !== undefined) g.compte[p.type_presence] += 1;
    });
    return [...groupes.values()];
  }, [presences]);

  const type = (v) => TYPES_PRESENCE.find((t) => t.value === v) || { label: v, couleur: "#94a3b8", icone: "fa-circle" };

  return (
    <Layout>
      <div className="lab-page">
        <header className="lab-header">
          <div>
            <h1><i className="fa-solid fa-clipboard-check"></i> Mes présences</h1>
            <p className="lab-sous-titre">Votre présence, séance par séance, pour chacune de vos sessions</p>
          </div>
        </header>

        {chargement && <div className="lab-etat"><i className="fa-solid fa-spinner fa-spin"></i> Chargement…</div>}
        {erreur && <div className="lab-flash erreur">Impossible de charger vos présences.</div>}
        {!chargement && !erreur && !parSession.length && (
          <div className="lab-etat">Aucune présence enregistrée pour le moment.</div>
        )}

        {parSession.map((g) => {
          const total = g.lignes.length;
          const taux = total ? Math.round(((g.compte.present + g.compte.retard) / total) * 100) : 0;
          return (
            <section key={g.nom} className="mes-presences-carte">
              <h2 style={{ fontSize: "1rem", margin: "0 0 4px" }}>{g.nom}</h2>
              <p className="lab-sous-titre" style={{ margin: "0 0 10px" }}>{g.formation}</p>
              <p style={{ display: "flex", gap: 16, flexWrap: "wrap", margin: "0 0 10px", fontSize: "0.85rem" }}>
                {TYPES_PRESENCE.map((t) => (
                  <span key={t.value} style={{ color: t.couleur }}>
                    <i className={`fa-solid ${t.icone}`}></i> {t.label} : <strong>{g.compte[t.value]}</strong>
                  </span>
                ))}
                <span>Taux de présence : <strong>{taux} %</strong></span>
              </p>
              <table className="mes-presences-table">
                <thead><tr><th>Date</th><th>Statut</th><th>Justification</th></tr></thead>
                <tbody>
                  {g.lignes.map((p) => (
                    <tr key={p.id}>
                      <td>{dateFr(p.date)}</td>
                      <td style={{ color: type(p.type_presence).couleur }}>
                        <i className={`fa-solid ${type(p.type_presence).icone}`}></i> {type(p.type_presence).label}
                      </td>
                      <td>{p.justification || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>
    </Layout>
  );
}

export default function Presences() {
  return authService.isApprenant() ? <MesPresences /> : <FeuilleAppel />;
}
